import { motion } from "framer-motion";
import { Store, TrendingDown, Megaphone, Users, Check } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { PageHero } from "../components/pt/PageHero";
import { ClosingCTA } from "../components/pt/ClosingCTA";
import { Calculator } from "../components/pt/Calculator";
import { useDocumentTitle } from "../lib/useDocumentTitle";

const BENEFIT_ICONS = [TrendingDown, Store, Megaphone, Users];

const Commerce = () => {
    const { t } = useLang();
    const p = t.commercePage;
    useDocumentTitle("/comercios");
    // piece 1 marker: bottom-left on
    const piecePattern = [
        ["", ""],
        ["", ""],
        ["on", "gap"],
    ];

    return (
        <div data-testid="commerce-page">
            <PageHero
                audienceTag={p.audienceTag}
                title={p.title}
                lead={p.lead}
                piecePattern={piecePattern}
                testId="commerce-hero"
            />

            {/* Benefits */}
            <section className="sec" data-testid="commerce-benefits">
                <div className="wrap">
                    <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
                        {p.benefits.map((b, i) => {
                            const Icon = BENEFIT_ICONS[i] || Store;
                            return (
                                <motion.div
                                    key={i}
                                    initial={{ opacity: 0, y: 24 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, amount: 0.3 }}
                                    transition={{ duration: 0.5, delay: i * 0.08 }}
                                    className="group rounded-xl border border-linea bg-white p-6 transition-all hover:-translate-y-1 hover:shadow-[0_30px_60px_-30px_rgba(20,35,64,0.3)]"
                                    data-testid={`commerce-benefit-${i}`}
                                >
                                    <div className="mb-5 flex h-11 w-11 items-center justify-center rounded-lg bg-niebla text-coral-profundo transition-colors group-hover:bg-coral-profundo group-hover:text-white">
                                        <Icon size={20} />
                                    </div>
                                    <h3 className="h3 mb-2">{b.title}</h3>
                                    <p className="text-[0.98rem] text-acero">
                                        {b.body}
                                    </p>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Time comparison table */}
            <section className="sec mist" data-testid="commerce-time-table">
                <div className="wrap grid gap-14 lg:grid-cols-[1fr_1.3fr] lg:items-start">
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{ duration: 0.6 }}
                    >
                        <h2 className="h2 max-w-[18ch]">
                            {p.timeTable.title}
                        </h2>
                        <p className="lead mt-4">{p.timeTable.lead}</p>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.2 }}
                        transition={{ duration: 0.6, delay: 0.15 }}
                        className="overflow-x-auto rounded-2xl border border-linea bg-white p-5 shadow-[0_30px_80px_-40px_rgba(20,35,64,0.25)]"
                    >
                        <table
                            className="tab-nums w-full border-collapse text-left"
                            data-testid="commerce-table"
                        >
                            <thead>
                                <tr className="text-xs uppercase tracking-wider text-acero">
                                    {p.timeTable.header.map((h, i) => (
                                        <th
                                            key={i}
                                            className={`pb-3 ${i > 0 ? "text-right" : ""}`}
                                        >
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {p.timeTable.rows.map((r, i) => {
                                    const isTotal = i === p.timeTable.rows.length - 1;
                                    return (
                                        <tr
                                            key={i}
                                            className={`${
                                                isTotal
                                                    ? "border-t-2 border-tinta font-bold text-tinta"
                                                    : "border-t border-linea"
                                            }`}
                                        >
                                            {r.map((cell, j) => (
                                                <td
                                                    key={j}
                                                    className={`py-3 text-[0.96rem] ${
                                                        j === 0
                                                            ? "pr-6 text-tinta"
                                                            : "text-right"
                                                    } ${
                                                        j === 2 && !isTotal
                                                            ? "font-semibold text-verde"
                                                            : ""
                                                    }`}
                                                >
                                                    {cell}
                                                </td>
                                            ))}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        <p className="mt-4 text-sm text-acero">
                            {p.timeTable.footnote}
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Setup / Who puts what */}
            <section className="sec" data-testid="commerce-setup">
                <div className="wrap">
                    <h2 className="h2 mb-10 max-w-[20ch]">{p.setup.title}</h2>
                    <div className="grid gap-6 md:grid-cols-2">
                        {[p.setup.left, p.setup.right].map((col, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.3 }}
                                transition={{ duration: 0.5, delay: i * 0.1 }}
                                className="rounded-2xl border-t-2 border-tinta bg-niebla p-7"
                            >
                                <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-coral-profundo">
                                    <Check size={16} />
                                    {col.h}
                                </div>
                                <p className="text-[1.05rem] text-tinta">
                                    {col.p}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Steps */}
            <section className="sec mist" data-testid="commerce-steps">
                <div className="wrap">
                    <div className="mb-10 max-w-2xl">
                        <h2 className="h2">{p.steps.title}</h2>
                        <p className="lead">{p.steps.lead}</p>
                    </div>
                    <div className="grid gap-6 md:grid-cols-3">
                        {p.steps.items.map((s, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.3 }}
                                transition={{ duration: 0.5, delay: i * 0.1 }}
                                className="rounded-xl bg-white p-7 shadow-[0_1px_0_var(--pt-linea)]"
                                data-testid={`commerce-step-${i}`}
                            >
                                <div
                                    className="mb-4 text-sm font-semibold text-coral-profundo tab-nums"
                                    style={{ fontStretch: "118%" }}
                                >
                                    {s.n}
                                </div>
                                <h3 className="h3 mb-2">{s.h}</h3>
                                <p className="text-[0.98rem] text-acero">
                                    {s.p}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Calculator */}
            <section id="calculadora" className="sec mist" data-testid="commerce-calculator">
                <div className="wrap">
                    <div className="mb-10 max-w-3xl">
                        <div className="pt-eyebrow mb-4">
                            <span className="dot" />
                            <span>{t.calc.commerce.hero.eyebrow}</span>
                        </div>
                        <h2 className="h2">{t.calc.commerce.hero.title}</h2>
                        <p className="lead mt-4">
                            {t.calc.commerce.hero.lead}
                        </p>
                    </div>
                    <Calculator type="comercio" />
                </div>
            </section>

            <ClosingCTA testId="commerce-closing" />
        </div>
    );
};

export default Commerce;
